import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { todayLocal } from '../lib/utils';
import Layout from '../components/Layout';
import ScannerEntradaModal from '../components/ScannerEntradaModal';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import { 
  Package, 
  Plus, 
  Search, 
  Upload, 
  ArrowDownToLine, 
  ArrowUpFromLine,
  History,
  AlertTriangle,
  TrendingDown,
  TrendingUp,
  FileText,
  Pill,
  Box,
  Edit,
  Trash2,
  Eye,
  Check,
  X,
  RefreshCw,
  ChevronRight,
  FileSpreadsheet,
  Download,
  FileDown,
  Users,
  Calendar,
  Clock,
  Layers,
  Barcode,
  Power,
  XCircle,
  RotateCcw,
  Loader2,
  Wrench
} from 'lucide-react';

// Labels for stock alert classifications
const CLASSIFICACAO_LABELS = {
  critico: 'Crítico',
  moderado: 'Moderado',
  satisfatorio: 'Satisfatório',
  sem_consumo: 'Sem consumo'
};

const CLASSIFICACAO_COLORS = {
  critico: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  moderado: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  satisfatorio: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  sem_consumo: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
};

// Labels for expiration status
const VALIDADE_STATUS_LABELS = {
  ok: 'OK',
  vence_em_breve: 'Vence em breve',
  vencido: 'Vencido'
};

const VALIDADE_STATUS_COLORS = {
  ok: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  vence_em_breve: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  vencido: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
};

const TIPO_LABELS = {
  material: 'Material',
  medicamento: 'Medicamento'
};

const Estoque = () => {
  const { api, user } = useAuth();
  const isAdmin = ['admin', 'master_admin', 'Master Admin'].includes(user?.perfil);

  const downloadReport = async (path, filename) => {
    try {
      const res = await api.get(path, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Erro ao exportar relatório');
    }
  };

  const getErrorMsg = (error, fallback = 'Erro') => {
    const detail = error?.response?.data?.detail;
    if (!detail) return fallback;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map(d => typeof d === 'string' ? d : (d.msg || JSON.stringify(d))).join('; ');
    return fallback;
  };

  const [activeTab, setActiveTab] = useState('itens');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('nome_asc'); // P2: Sorting state
  const [ativoFilter, setAtivoFilter] = useState('todos'); // ativo filter
  
  // Data states
  const [itens, setItens] = useState([]);
  const [entradas, setEntradas] = useState([]);
  const [saidas, setSaidas] = useState([]);
  const [movimentacoes, setMovimentacoes] = useState([]);
  const [alertas, setAlertas] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [consumoPacientes, setConsumoPacientes] = useState(null);
  const [consumoMensal, setConsumoMensal] = useState(null);
  const [consumoEquipes, setConsumoEquipes] = useState(null);
  const [showImportCustosDialog, setShowImportCustosDialog] = useState(false);
  const [custosCSVData, setCustosCSVData] = useState([]);
  const [importandoCustos, setImportandoCustos] = useState(false);
  
  // Dialog states
  const [showItemDialog, setShowItemDialog] = useState(false);
  const [showEntradaDialog, setShowEntradaDialog] = useState(false);
  const [showSaidaDialog, setShowSaidaDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [showScannerModal, setShowScannerModal] = useState(false); // Scanner modal
  const [editingItem, setEditingItem] = useState(null);
  const [editingEntrada, setEditingEntrada] = useState(null);
  const [showEditEntradaDialog, setShowEditEntradaDialog] = useState(false);
  const [showZerarDialog, setShowZerarDialog] = useState(false);
  const [zerarItemId, setZerarItemId] = useState(null);
  const [zerarForm, setZerarForm] = useState({ motivo: '', confirmacao: '' });
  const [savingZerar, setSavingZerar] = useState(false);
  const [showReverterDialog, setShowReverterDialog] = useState(false);
  const [reverterInfo, setReverterInfo] = useState(null);
  const [loadingReverter, setLoadingReverter] = useState(false);
  
  // Form states
  const [itemForm, setItemForm] = useState({
    tipo: 'material',
    nome: '',
    categoria: '',
    unidade: 'un',
    codigo_interno: '',
    codigo_barras: '',
    fabricante: '',
    estoque_minimo: '',
    concentracao: '',
    forma: ''
  });
  
  const [entradaForm, setEntradaForm] = useState({
    fornecedor: '',
    documento_ref: '',
    data_entrada: todayLocal(),
    observacoes: '',
    itens: [{ item_id: '', quantidade: '', valor_unitario: '', valor_total: '', lote: '', validade: '' }]
  });

  const [editEntradaForm, setEditEntradaForm] = useState({
    fornecedor: '',
    documento_ref: '',
    data_entrada: '',
    observacoes: ''
  });
  
  const [saidaForm, setSaidaForm] = useState({
    item_id: '',
    quantidade: '',
    motivo: '',
    referencia: ''
  });

  // Lotes (batches) state
  const [lotes, setLotes] = useState([]);
  const [selectedItemLotes, setSelectedItemLotes] = useState(null);
  const [showLotesDialog, setShowLotesDialog] = useState(false);
  
  // Import states
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importedData, setImportedData] = useState(null);
  const [showColumnMappingDialog, setShowColumnMappingDialog] = useState(false);
  const [columnData, setColumnData] = useState(null);
  const [columnMapping, setColumnMapping] = useState({
    nome: '',
    quantidade: '',
    valor_unitario: '',
    lote: '',
    validade: ''
  });
  
  // Stock config state
  const [estoqueConfig, setEstoqueConfig] = useState({
    metodo_baixa: 'fefo',
    janela_vence_em_breve_dias: 30
  });
  
  // Fetch data
  useEffect(() => {
    fetchData();
  }, [activeTab]);
  
  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'itens') {
        const response = await api.get('/estoque/itens');
        setItens(response.data);
      } else if (activeTab === 'entradas') {
        const response = await api.get('/estoque/entradas');
        setEntradas(response.data);
      } else if (activeTab === 'saidas') {
        const response = await api.get('/estoque/saidas');
        setSaidas(response.data);
      } else if (activeTab === 'movimentacoes') {
        const response = await api.get('/estoque/movimentacoes');
        setMovimentacoes(response.data);
      } else if (activeTab === 'alertas') {
        const [alertasRes, dashRes] = await Promise.all([
          api.get('/estoque/alertas'),
          api.get('/estoque/dashboard')
        ]);
        setAlertas(alertasRes.data);
        setDashboard(dashRes.data);
      } else if (activeTab === 'consumo') {
        const [pacRes, mensalRes, equipeRes] = await Promise.all([
          api.get('/estoque/consumo-pacientes'),
          api.get('/estoque/consumo-mensal'),
          api.get('/estoque/consumo-equipes')
        ]);
        setConsumoPacientes(pacRes.data);
        setConsumoMensal(mensalRes.data);
        setConsumoEquipes(equipeRes.data);
      }
    } catch (error) {
      toast.error('Erro ao carregar dados');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch items for selects
  const fetchItens = async () => {
    try {
      const response = await api.get('/estoque/itens');
      setItens(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleToggleAtivo = async (itemId) => {
    try {
      const res = await api.put(`/estoque/itens/${itemId}/toggle-ativo`);
      toast.success(res.data.message);
      setItens(prev => prev.map(i => i.id === itemId ? { ...i, ativo: res.data.ativo } : i));
    } catch (error) {
      toast.error('Erro ao alterar status');
    }
  };

  // Fetch lotes for an item
  const fetchItemLotes = async (itemId) => {
    try {
      const response = await api.get(`/estoque/itens/${itemId}/lotes`);
      setSelectedItemLotes(response.data);
      setShowLotesDialog(true);
    } catch (error) {
      toast.error('Erro ao carregar lotes');
      console.error(error);
    }
  };

  // Handle import file upload
  const handleImportFile = async (file) => {
    if (!file) return;
    
    const filename = file.name.toLowerCase();
    const isCsvOrExcel = filename.endsWith('.csv') || filename.endsWith('.xlsx') || filename.endsWith('.xls');
    
    setImportFile(file);
    setImportLoading(true);
    
    try {
      // For CSV/Excel, first detect columns
      if (isCsvOrExcel) {
        const formData = new FormData();
        formData.append('file', file);
        
        const colResponse = await api.post('/estoque/importar/colunas', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        setColumnData(colResponse.data);
        setShowColumnMappingDialog(true);
      } else {
        // For other formats, process directly
        await processImportFile(file);
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error(getErrorMsg(error, 'Erro ao processar arquivo'));
    } finally {
      setImportLoading(false);
    }
  };

  // Process import with optional column mapping
  const processImportFile = async (file, mapping = null) => {
    setImportLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Add mapping as query params if provided
      let url = '/estoque/importar/processar';
      if (mapping) {
        const params = new URLSearchParams();
        Object.entries(mapping).forEach(([key, value]) => {
          if (value) params.append(`mapeamento_${key}`, value);
        });
        if (params.toString()) url += `?${params.toString()}`;
      }
      
      const response = await api.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setImportedData(response.data);
      setShowReviewDialog(true);
      setShowColumnMappingDialog(false);
      setShowImportDialog(false);
      
      if (response.data.alertas?.length > 0) {
        response.data.alertas.forEach(alerta => toast.warning(alerta));
      }
      
      toast.success(`${response.data.itens?.length || 0} itens extraídos para revisão`);
    } catch (error) {
      console.error('Import error:', error);
      toast.error(getErrorMsg(error, 'Erro ao processar arquivo'));
    } finally {
      setImportLoading(false);
    }
  };

  // Confirm and create import entry
  const handleConfirmImport = async () => {
    if (!importedData?.itens?.length) {
      toast.error('Nenhum item para importar');
      return;
    }
    
    // Check for medications without validade
    const medicamentosSemValidade = importedData.itens.filter(
      item => item.tipo === 'medicamento' && !item.validade
    );
    
    if (medicamentosSemValidade.length > 0) {
      toast.error('Medicamentos precisam ter validade informada. Preencha os campos em vermelho.');
      return;
    }
    
    setImportLoading(true);
    
    try {
      // Convert to entrada format
      const entradaData = {
        fornecedor: importedData.fornecedor || 'Importação via arquivo',
        documento_ref: importedData.documento_ref,
        data_entrada: importedData.data_documento || todayLocal(),
        itens: importedData.itens.map(item => ({
          item_id: item.item_id,
          item_nome: item.item_nome,
          criar_novo: item.criar_novo,
          novo_item: item.criar_novo ? {
            tipo: item.tipo,
            nome: item.item_nome,
            unidade: 'un'
          } : undefined,
          quantidade: item.quantidade,
          valor_unitario: item.valor_unitario,
          lote: item.lote,
          validade: item.validade
        }))
      };
      
      await api.post('/estoque/entradas', entradaData);
      
      toast.success('Entrada criada com sucesso!');
      setShowReviewDialog(false);
      setImportedData(null);
      setImportFile(null);
      fetchData();
    } catch (error) {
      console.error('Confirm import error:', error);
      toast.error(getErrorMsg(error, 'Erro ao criar entrada'));
    } finally {
      setImportLoading(false);
    }
  };

  // Update imported item
  const updateImportedItem = (index, field, value) => {
    setImportedData(prev => {
      const newItens = [...prev.itens];
      newItens[index] = { ...newItens[index], [field]: value };
      return { ...prev, itens: newItens };
    });
  };
  
  // Filter items by search
  const filteredItens = useMemo(() => {
    let result = itens;
    
    // Filter by ativo status
    if (ativoFilter === 'ativo') {
      result = result.filter(item => item.ativo !== false);
    } else if (ativoFilter === 'inativo') {
      result = result.filter(item => item.ativo === false);
    }
    
    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(item => 
        item.nome?.toLowerCase().includes(term) ||
        item.codigo_interno?.toLowerCase().includes(term) ||
        item.codigo_barras?.toLowerCase().includes(term)
      );
    }
    
    // P2: Sort results
    const sorted = [...result];
    switch (sortBy) {
      case 'nome_asc':
        return sorted.sort((a, b) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR'));
      case 'nome_desc':
        return sorted.sort((a, b) => (b.nome || '').localeCompare(a.nome || '', 'pt-BR'));
      case 'estoque_asc':
        return sorted.sort((a, b) => (a.estoque_atual || 0) - (b.estoque_atual || 0));
      case 'estoque_desc':
        return sorted.sort((a, b) => (b.estoque_atual || 0) - (a.estoque_atual || 0));
      case 'tipo_asc':
        return sorted.sort((a, b) => (a.tipo || '').localeCompare(b.tipo || '', 'pt-BR'));
      case 'tipo_desc':
        return sorted.sort((a, b) => (b.tipo || '').localeCompare(a.tipo || '', 'pt-BR'));
      case 'codigo_asc':
        return sorted.sort((a, b) => (a.codigo_interno || '').localeCompare(b.codigo_interno || '', 'pt-BR'));
      case 'codigo_desc':
        return sorted.sort((a, b) => (b.codigo_interno || '').localeCompare(a.codigo_interno || '', 'pt-BR'));
      default:
        return sorted;
    }
  }, [itens, searchTerm, sortBy, ativoFilter]);
  
  // Handle item form
  const handleItemSubmit = async () => {
    try {
      // Clean empty strings to null for optional numeric fields
      const payload = { ...itemForm };
      if (payload.estoque_minimo === '' || payload.estoque_minimo === null) {
        payload.estoque_minimo = null;
      } else {
        payload.estoque_minimo = parseFloat(payload.estoque_minimo) || null;
      }
      // Clean custo_medio
      if (payload.custo_medio === '' || payload.custo_medio === null || payload.custo_medio === undefined) {
        delete payload.custo_medio;
      } else {
        payload.custo_medio = parseFloat(payload.custo_medio) || 0;
      }
      // Remove empty optional strings
      ['codigo_interno', 'codigo_barras', 'fabricante', 'concentracao', 'forma', 'categoria', 'lote', 'validade'].forEach(k => {
        if (payload[k] === '') payload[k] = null;
      });

      if (editingItem) {
        await api.put(`/estoque/itens/${editingItem.id}`, payload);
        toast.success('Item atualizado com sucesso');
      } else {
        await api.post('/estoque/itens', payload);
        toast.success('Item criado com sucesso');
      }
      setShowItemDialog(false);
      resetItemForm();
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map(d => d.msg || d).join(', ') : 'Erro ao salvar item');
      toast.error(msg);
    }
  };
  
  const resetItemForm = () => {
    setItemForm({
      tipo: 'material',
      nome: '',
      categoria: '',
      unidade: 'un',
      codigo_interno: '',
      codigo_barras: '',
      fabricante: '',
      estoque_minimo: '',
      concentracao: '',
      forma: '',
      custo_medio: '',
      lote: '',
      validade: ''
    });
    setEditingItem(null);
  };
  
  const handleEditItem = (item) => {
    setEditingItem(item);
    setItemForm({
      tipo: item.tipo || 'material',
      nome: item.nome || '',
      categoria: item.categoria || '',
      unidade: item.unidade || 'un',
      codigo_interno: item.codigo_interno || '',
      codigo_barras: item.codigo_barras || '',
      fabricante: item.fabricante || '',
      estoque_minimo: item.estoque_minimo ?? '',
      concentracao: item.concentracao || '',
      forma: item.forma || '',
      custo_medio: item.custo_medio ?? '',
      lote: item.lote || '',
      validade: item.validade || ''
    });
    setShowItemDialog(true);
  };

  // Edit Entrada functions
  const handleEditEntrada = (entrada) => {
    setEditingEntrada(entrada);
    setEditEntradaForm({
      fornecedor: entrada.fornecedor || '',
      documento_ref: entrada.documento_ref || '',
      data_entrada: entrada.data_entrada || '',
      observacoes: entrada.observacoes || ''
    });
    setShowEditEntradaDialog(true);
  };

  const handleSaveEntrada = async () => {
    if (!editingEntrada) return;
    
    try {
      await api.put(`/estoque/entradas/${editingEntrada.id}`, editEntradaForm);
      toast.success('Entrada atualizada com sucesso');
      setShowEditEntradaDialog(false);
      setEditingEntrada(null);
      fetchData();
    } catch (error) {
      console.error('Error updating entrada:', error);
      toast.error(getErrorMsg(error, 'Erro ao atualizar entrada'));
    }
  };

  const handleDeleteEntrada = async (entradaId) => {
    if (!confirm('Tem certeza que deseja excluir esta entrada? Esta ação não reverte o estoque.')) return;
    
    try {
      await api.delete(`/estoque/entradas/${entradaId}`);
      toast.success('Entrada excluída com sucesso');
      fetchData();
    } catch (error) {
      console.error('Error deleting entrada:', error);
      toast.error(getErrorMsg(error, 'Erro ao excluir entrada'));
    }
  };
  
  const handleDeleteItem = async (itemId) => {
    if (!confirm('Tem certeza que deseja excluir este item?')) return;
    try {
      await api.delete(`/estoque/itens/${itemId}`);
      toast.success('Item excluído com sucesso');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir item');
    }
  };

  const handleOpenZerar = (itemId) => {
    setZerarItemId(itemId);
    setZerarForm({ motivo: '', confirmacao: '' });
    setShowZerarDialog(true);
  };

  const handleZerarEstoque = async () => {
    if (zerarForm.confirmacao !== 'ZERAR') {
      toast.error("Digite 'ZERAR' para confirmar");
      return;
    }
    if (!zerarForm.motivo || zerarForm.motivo.trim().length < 5) {
      toast.error('Informe o motivo do ajuste (mínimo 5 caracteres)');
      return;
    }
    setSavingZerar(true);
    try {
      const res = await api.post(`/estoque/itens/${zerarItemId}/zerar`, zerarForm);
      toast.success(res.data.message || 'Estoque zerado com sucesso');
      setShowZerarDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao zerar estoque');
    } finally {
      setSavingZerar(false);
    }
  };

  const handleCheckReverter = async () => {
    setLoadingReverter(true);
    try {
      const res = await api.get('/estoque/movimentacoes-auto');
      setReverterInfo(res.data);
      setShowReverterDialog(true);
    } catch (err) {
      toast.error('Erro ao carregar movimentações automáticas');
    } finally {
      setLoadingReverter(false);
    }
  };

  const handleReverterBaixas = async () => {
    setLoadingReverter(true);
    try {
      const res = await api.post('/estoque/reverter-baixas-automaticas');
      toast.success(res.data.message);
      setReverterInfo(null);
      setShowReverterDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao reverter baixas');
    } finally {
      setLoadingReverter(false);
    }
  };
  
  // Handle entrada form
  const handleEntradaSubmit = async () => {
    try {
      const payload = {
        ...entradaForm,
        itens: entradaForm.itens.filter(i => i.item_id && i.quantidade).map(i => ({
          item_id: i.item_id,
          quantidade: parseFloat(i.quantidade),
          valor_unitario: i.valor_unitario ? parseFloat(i.valor_unitario) : null,
          valor_total: i.valor_total ? parseFloat(i.valor_total) : null
        }))
      };
      await api.post('/estoque/entradas', payload);
      toast.success('Entrada registrada com sucesso');
      setShowEntradaDialog(false);
      resetEntradaForm();
      fetchData();
    } catch (error) {
      toast.error(getErrorMsg(error, 'Erro ao registrar entrada'));
    }
  };
  
  const resetEntradaForm = () => {
    setEntradaForm({
      fornecedor: '',
      documento_ref: '',
      data_entrada: todayLocal(),
      observacoes: '',
      itens: [{ item_id: '', quantidade: '', valor_unitario: '', valor_total: '' }]
    });
  };
  
  const addEntradaLine = () => {
    setEntradaForm(prev => ({
      ...prev,
      itens: [...prev.itens, { item_id: '', quantidade: '', valor_unitario: '', valor_total: '' }]
    }));
  };
  
  const updateEntradaLine = (index, field, value) => {
    setEntradaForm(prev => {
      const newItens = [...prev.itens];
      newItens[index] = { ...newItens[index], [field]: value };
      
      // Auto-calculate values
      if (field === 'valor_unitario' && newItens[index].quantidade) {
        newItens[index].valor_total = (parseFloat(value) * parseFloat(newItens[index].quantidade)).toFixed(2);
      } else if (field === 'valor_total' && newItens[index].quantidade) {
        newItens[index].valor_unitario = (parseFloat(value) / parseFloat(newItens[index].quantidade)).toFixed(4);
      } else if (field === 'quantidade') {
        if (newItens[index].valor_unitario) {
          newItens[index].valor_total = (parseFloat(newItens[index].valor_unitario) * parseFloat(value)).toFixed(2);
        } else if (newItens[index].valor_total) {
          newItens[index].valor_unitario = (parseFloat(newItens[index].valor_total) / parseFloat(value)).toFixed(4);
        }
      }
      
      return { ...prev, itens: newItens };
    });
  };
  
  const removeEntradaLine = (index) => {
    setEntradaForm(prev => ({
      ...prev,
      itens: prev.itens.filter((_, i) => i !== index)
    }));
  };
  
  // Handle saida form
  const handleSaidaSubmit = async () => {
    try {
      const payload = {
        ...saidaForm,
        quantidade: parseFloat(saidaForm.quantidade)
      };
      await api.post('/estoque/saidas', payload);
      toast.success('Saída registrada com sucesso');
      setShowSaidaDialog(false);
      resetSaidaForm();
      fetchData();
    } catch (error) {
      toast.error(getErrorMsg(error, 'Erro ao registrar saída'));
    }
  };
  
  const resetSaidaForm = () => {
    setSaidaForm({ item_id: '', quantidade: '', motivo: '', referencia: '' });
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const d = /^\d{4}-\d{2}-\d{2}$/.test(dateStr) ? new Date(dateStr + 'T12:00:00') : new Date(dateStr); return d.toLocaleDateString('pt-BR');
    } catch {
      return dateStr;
    }
  };
  
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  return (
    <Layout>
      <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold">Estoque</h1>
          <p className="text-muted-foreground">Controle de materiais e medicamentos</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => { fetchItens(); setShowSaidaDialog(true); }}>
            <ArrowUpFromLine className="h-4 w-4 mr-2" />
            Nova Saída
          </Button>
          <Button variant="outline" onClick={() => { fetchItens(); setShowEntradaDialog(true); }}>
            <ArrowDownToLine className="h-4 w-4 mr-2" />
            Nova Entrada
          </Button>
          <Button variant="outline" onClick={() => setShowScannerModal(true)} data-testid="btn-scanner">
            <Barcode className="h-4 w-4 mr-2" />
            Entrada por Scanner
          </Button>
          <Button onClick={() => setShowImportDialog(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Importar (IA)
          </Button>
          {isAdmin && (
            <Button 
              variant="outline" 
              className="border-amber-400 text-amber-700 hover:bg-amber-50"
              onClick={handleCheckReverter}
              disabled={loadingReverter}
              data-testid="btn-reverter-baixas"
            >
              {loadingReverter ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-2" />}
              Reverter baixas automáticas
            </Button>
          )}
        </div>
      </div>

      {/* Dashboard Cards */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4 text-blue-500" />
                <span className="text-sm text-muted-foreground">Total Itens</span>
              </div>
              <p className="text-2xl font-bold">{dashboard.totais.itens}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-red-500">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-red-500" />
                <span className="text-sm text-muted-foreground">Crítico</span>
              </div>
              <p className="text-2xl font-bold text-red-600">{dashboard.alertas.critico}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-yellow-500">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <TrendingDown className="h-4 w-4 text-yellow-500" />
                <span className="text-sm text-muted-foreground">Moderado</span>
              </div>
              <p className="text-2xl font-bold text-yellow-600">{dashboard.alertas.moderado}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-green-500" />
                <span className="text-sm text-muted-foreground">Satisfatório</span>
              </div>
              <p className="text-2xl font-bold text-green-600">{dashboard.alertas.satisfatorio}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="itens" data-testid="tab-itens">
            <Box className="h-4 w-4 mr-2" />
            Itens
          </TabsTrigger>
          <TabsTrigger value="entradas" data-testid="tab-entradas">
            <ArrowDownToLine className="h-4 w-4 mr-2" />
            Entradas
          </TabsTrigger>
          <TabsTrigger value="saidas" data-testid="tab-saidas">
            <ArrowUpFromLine className="h-4 w-4 mr-2" />
            Saídas
          </TabsTrigger>
          <TabsTrigger value="movimentacoes" data-testid="tab-movimentacoes">
            <History className="h-4 w-4 mr-2" />
            Movimentações
          </TabsTrigger>
          <TabsTrigger value="alertas" data-testid="tab-alertas">
            <AlertTriangle className="h-4 w-4 mr-2" />
            Alertas
          </TabsTrigger>
          <TabsTrigger value="relatorios" data-testid="tab-relatorios">
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Relatórios
          </TabsTrigger>
          <TabsTrigger value="consumo" data-testid="tab-consumo">
            <Users className="h-4 w-4 mr-2" />
            Consumo Pacientes
          </TabsTrigger>
        </TabsList>

        {/* Itens Tab */}
        <TabsContent value="itens" className="space-y-4">
          <div className="flex flex-wrap gap-4 justify-between items-center">
            <div className="flex gap-2 items-center">
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar item..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
              {/* P2: Sorting dropdown */}
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-44" data-testid="sort-select">
                  <SelectValue placeholder="Ordenar por" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="nome_asc">Nome (A–Z)</SelectItem>
                  <SelectItem value="nome_desc">Nome (Z–A)</SelectItem>
                  <SelectItem value="estoque_desc">Estoque (Maior)</SelectItem>
                  <SelectItem value="estoque_asc">Estoque (Menor)</SelectItem>
                  <SelectItem value="tipo_asc">Tipo (A–Z)</SelectItem>
                  <SelectItem value="tipo_desc">Tipo (Z–A)</SelectItem>
                  <SelectItem value="codigo_asc">Código (A–Z)</SelectItem>
                  <SelectItem value="codigo_desc">Código (Z–A)</SelectItem>
                </SelectContent>
              </Select>
              {/* Ativo/Inativo filter */}
              <Select value={ativoFilter} onValueChange={setAtivoFilter}>
                <SelectTrigger className="w-36" data-testid="filter-ativo">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="ativo">Ativos</SelectItem>
                  <SelectItem value="inativo">Inativos</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowImportCustosDialog(true)} data-testid="btn-importar-custos">
                <Upload className="h-4 w-4 mr-2" />
                Importar Custos (CSV)
              </Button>
              <Button onClick={() => { resetItemForm(); setShowItemDialog(true); }}>
                <Plus className="h-4 w-4 mr-2" />
                Novo Item
              </Button>
            </div>
          </div>
          
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Categoria</TableHead>
                    <TableHead className="text-right">Estoque</TableHead>
                    <TableHead className="text-right">Custo Médio</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    [...Array(5)].map((_, i) => (
                      <TableRow key={i}>
                        <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                      </TableRow>
                    ))
                  ) : filteredItens.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        Nenhum item encontrado
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredItens.map(item => (
                      <TableRow key={item.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            {item.tipo === 'medicamento' ? (
                              <Pill className="h-4 w-4 text-purple-500" />
                            ) : (
                              <Box className="h-4 w-4 text-blue-500" />
                            )}
                            {item.nome}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {TIPO_LABELS[item.tipo]}
                          </Badge>
                        </TableCell>
                        <TableCell>{item.categoria || '-'}</TableCell>
                        <TableCell className="text-right">
                          {item.estoque_atual} {item.unidade}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(item.custo_medio)}
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant={item.ativo !== false ? 'default' : 'secondary'}
                            className={item.ativo !== false ? 'bg-green-100 text-green-800 hover:bg-green-200' : 'bg-gray-100 text-gray-500'}
                          >
                            {item.ativo !== false ? 'Ativo' : 'Inativo'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleToggleAtivo(item.id)}
                              title={item.ativo !== false ? 'Desativar item' : 'Ativar item'}
                              data-testid={`btn-toggle-ativo-${item.id}`}
                            >
                              <Power className={`h-4 w-4 ${item.ativo !== false ? 'text-green-500' : 'text-gray-400'}`} />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => fetchItemLotes(item.id)}
                              title="Ver lotes"
                            >
                              <Layers className="h-4 w-4 text-orange-500" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleEditItem(item)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            {isAdmin && item.estoque_atual > 0 && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => handleOpenZerar(item.id)}
                                title="Zerar estoque"
                                data-testid={`btn-zerar-${item.id}`}
                              >
                                <XCircle className="h-4 w-4 text-orange-600" />
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteItem(item.id)}>
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Entradas Tab */}
        <TabsContent value="entradas" className="space-y-4">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead>
                    <TableHead>Fornecedor</TableHead>
                    <TableHead>Documento</TableHead>
                    <TableHead className="text-right">Itens</TableHead>
                    <TableHead className="text-right">Valor Total</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    [...Array(5)].map((_, i) => (
                      <TableRow key={i}>
                        <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-12" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                      </TableRow>
                    ))
                  ) : entradas.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        Nenhuma entrada registrada
                      </TableCell>
                    </TableRow>
                  ) : (
                    entradas.map(entrada => (
                      <TableRow key={entrada.id}>
                        <TableCell>{formatDate(entrada.data_entrada)}</TableCell>
                        <TableCell>{entrada.fornecedor || '-'}</TableCell>
                        <TableCell>{entrada.documento_ref || '-'}</TableCell>
                        <TableCell className="text-right">{entrada.itens_count}</TableCell>
                        <TableCell className="text-right">{formatCurrency(entrada.valor_total)}</TableCell>
                        <TableCell>
                          <Badge variant="default">{entrada.status}</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleEditEntrada(entrada)}
                              data-testid={`edit-entrada-${entrada.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleDeleteEntrada(entrada.id)}
                              className="text-destructive hover:text-destructive"
                              data-testid={`delete-entrada-${entrada.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Saídas Tab */}
        <TabsContent value="saidas" className="space-y-4">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead>
                    <TableHead>Item</TableHead>
                    <TableHead className="text-right">Quantidade</TableHead>
                    <TableHead>Motivo</TableHead>
                    <TableHead>Referência</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    [...Array(5)].map((_, i) => (
                      <TableRow key={i}>
                        <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                      </TableRow>
                    ))
                  ) : saidas.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        Nenhuma saída registrada
                      </TableCell>
                    </TableRow>
                  ) : (
                    saidas.map(saida => (
                      <TableRow key={saida.id}>
                        <TableCell>{formatDate(saida.data_saida)}</TableCell>
                        <TableCell>{saida.item_nome}</TableCell>
                        <TableCell className="text-right">{saida.quantidade}</TableCell>
                        <TableCell>{saida.motivo || '-'}</TableCell>
                        <TableCell>{saida.referencia || '-'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Movimentações Tab */}
        <TabsContent value="movimentacoes" className="space-y-4">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead>
                    <TableHead>Item</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead className="text-right">Quantidade</TableHead>
                    <TableHead className="text-right">Saldo</TableHead>
                    <TableHead>Referência</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    [...Array(5)].map((_, i) => (
                      <TableRow key={i}>
                        <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                      </TableRow>
                    ))
                  ) : movimentacoes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        Nenhuma movimentação registrada
                      </TableCell>
                    </TableRow>
                  ) : (
                    movimentacoes.map(mov => (
                      <TableRow key={mov.id}>
                        <TableCell>{formatDate(mov.criado_em)}</TableCell>
                        <TableCell>{mov.item_nome}</TableCell>
                        <TableCell>
                          <Badge variant={mov.tipo === 'entrada' ? 'default' : mov.tipo === 'saida' ? 'destructive' : 'secondary'}>
                            {mov.tipo === 'entrada' ? 'Entrada' : 
                             mov.tipo === 'saida' ? 'Saída' : 
                             mov.tipo === 'consumo_paciente' ? 'Consumo' : mov.tipo}
                          </Badge>
                        </TableCell>
                        <TableCell className={`text-right ${mov.quantidade > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {mov.quantidade > 0 ? '+' : ''}{mov.quantidade}
                        </TableCell>
                        <TableCell className="text-right">{mov.saldo_posterior}</TableCell>
                        <TableCell className="max-w-xs truncate">{mov.referencia || mov.paciente_nome || '-'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Alertas Tab */}
        <TabsContent value="alertas" className="space-y-4">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead className="text-right">Estoque Atual</TableHead>
                    <TableHead className="text-right">Consumo Médio/Dia</TableHead>
                    <TableHead className="text-right">Dias de Cobertura</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    [...Array(5)].map((_, i) => (
                      <TableRow key={i}>
                        <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                      </TableRow>
                    ))
                  ) : alertas.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        Nenhum alerta de estoque
                      </TableCell>
                    </TableRow>
                  ) : (
                    alertas.map(alerta => (
                      <TableRow key={alerta.item_id}>
                        <TableCell className="font-medium">{alerta.item_nome}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{TIPO_LABELS[alerta.tipo]}</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {alerta.estoque_atual} {alerta.unidade}
                        </TableCell>
                        <TableCell className="text-right">
                          {alerta.consumo_medio_diario} {alerta.unidade}
                        </TableCell>
                        <TableCell className="text-right">
                          {alerta.dias_cobertura !== null ? `${alerta.dias_cobertura} dias` : '-'}
                        </TableCell>
                        <TableCell>
                          <Badge className={CLASSIFICACAO_COLORS[alerta.classificacao]}>
                            {CLASSIFICACAO_LABELS[alerta.classificacao]}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Relatórios Tab */}
        <TabsContent value="relatorios" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5" />
                Exportar Relatórios
              </CardTitle>
              <CardDescription>
                Exporte dados do estoque em formato CSV ou Excel
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                {/* Items Report */}
                <Card className="border-2 border-dashed hover:border-primary/50 transition-colors">
                  <CardContent className="pt-6">
                    <div className="flex flex-col items-center text-center">
                      <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-full mb-3">
                        <Package className="h-6 w-6 text-blue-600" />
                      </div>
                      <h3 className="font-semibold mb-1">Cadastro de Itens</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Lista completa de materiais e medicamentos com estoque e valores
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadReport('/estoque/export/itens?formato=csv', 'estoque_itens.csv')}
                          data-testid="export-itens-csv"
                        >
                          <FileDown className="h-4 w-4 mr-1" />
                          CSV
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadReport('/estoque/export/itens?formato=excel', 'estoque_itens.xlsx')}
                          data-testid="export-itens-excel"
                        >
                          <FileSpreadsheet className="h-4 w-4 mr-1" />
                          Excel
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Movements Report */}
                <Card className="border-2 border-dashed hover:border-primary/50 transition-colors">
                  <CardContent className="pt-6">
                    <div className="flex flex-col items-center text-center">
                      <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-full mb-3">
                        <History className="h-6 w-6 text-purple-600" />
                      </div>
                      <h3 className="font-semibold mb-1">Movimentações</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Histórico de entradas, saídas e consumos com custos
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadReport('/estoque/export/movimentacoes?formato=csv&periodo=sempre', 'estoque_movimentacoes.csv')}
                          data-testid="export-mov-csv"
                        >
                          <FileDown className="h-4 w-4 mr-1" />
                          CSV
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadReport('/estoque/export/movimentacoes?formato=excel&periodo=sempre', 'estoque_movimentacoes.xlsx')}
                          data-testid="export-mov-excel"
                        >
                          <FileSpreadsheet className="h-4 w-4 mr-1" />
                          Excel
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Patient Consumption Report */}
                <Card className="border-2 border-dashed hover:border-primary/50 transition-colors">
                  <CardContent className="pt-6">
                    <div className="flex flex-col items-center text-center">
                      <div className="p-3 bg-orange-100 dark:bg-orange-900/30 rounded-full mb-3">
                        <Users className="h-6 w-6 text-orange-600" />
                      </div>
                      <h3 className="font-semibold mb-1">Consumo por Paciente</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Detalhamento de consumo de cada paciente
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadReport('/estoque/export/consumo-paciente?formato=csv&periodo=sempre', 'consumo_por_paciente.csv')}
                          data-testid="export-consumo-csv"
                        >
                          <FileDown className="h-4 w-4 mr-1" />
                          CSV
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadReport('/estoque/export/consumo-paciente?formato=excel&periodo=sempre', 'consumo_por_paciente.xlsx')}
                          data-testid="export-consumo-excel"
                        >
                          <FileSpreadsheet className="h-4 w-4 mr-1" />
                          Excel
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Dispensações Avulsas Report */}
                <Card className="border-2 border-dashed hover:border-primary/50 transition-colors">
                  <CardContent className="pt-6">
                    <div className="text-center space-y-3">
                      <div className="h-12 w-12 mx-auto rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center">
                        <Wrench className="h-6 w-6 text-teal-600" />
                      </div>
                      <h3 className="font-semibold">Dispensações Avulsas</h3>
                      <p className="text-sm text-muted-foreground">
                        MAT/MED Ambulâncias e Base com técnico responsável
                      </p>
                      <div className="flex gap-2 justify-center">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => downloadReport('/estoque/export/dispensacoes-avulsas?formato=csv&periodo=sempre', 'dispensacoes_avulsas.csv')}
                          data-testid="export-avulsos-csv"
                        >
                          <FileSpreadsheet className="h-3 w-3 mr-1" />
                          CSV
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => downloadReport('/estoque/export/dispensacoes-avulsas?formato=excel&periodo=sempre', 'dispensacoes_avulsas.xlsx')}
                          data-testid="export-avulsos-excel"
                        >
                          <FileSpreadsheet className="h-3 w-3 mr-1" />
                          Excel
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Consumo Pacientes Tab */}
        <TabsContent value="consumo" className="space-y-4">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : consumoPacientes ? (
            <>
              {/* Summary cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold">{consumoPacientes.total_pacientes}</p>
                    <p className="text-xs text-muted-foreground">Pacientes com consumo</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <p className="text-2xl font-bold">{consumoPacientes.total_geral_itens}</p>
                    <p className="text-xs text-muted-foreground">Total de itens consumidos</p>
                  </CardContent>
                </Card>
                {consumoPacientes.total_geral_custo != null && (
                  <Card>
                    <CardContent className="pt-4 text-center">
                      <p className="text-2xl font-bold text-red-600">{formatCurrency(consumoPacientes.total_geral_custo)}</p>
                      <p className="text-xs text-muted-foreground">Custo total de consumo</p>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Monthly Chart + Team Chart side by side */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Monthly evolution chart */}
                {consumoMensal?.mensal?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Calendar className="h-4 w-4" /> Evolução Mensal de Consumo
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={consumoMensal.mensal}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                          <YAxis yAxisId="left" allowDecimals={false} tick={{ fontSize: 11 }} />
                          <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                          <Tooltip formatter={(v, name) => [name === 'total_custo' ? `R$ ${Number(v).toFixed(2)}` : v, name === 'total_custo' ? 'Custo' : 'Itens']} />
                          <Legend />
                          <Bar yAxisId="left" dataKey="total_itens" name="Itens" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                          <Bar yAxisId="right" dataKey="total_custo" name="Custo (R$)" fill="#ef4444" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}

                {/* Team consumption chart */}
                {consumoEquipes?.por_equipe?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Users className="h-4 w-4" /> Custos por Equipe
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={consumoEquipes.por_equipe} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis type="number" tick={{ fontSize: 11 }} />
                          <YAxis type="category" dataKey="equipe_nome" width={100} tick={{ fontSize: 11 }} />
                          <Tooltip formatter={(v) => [`R$ ${Number(v).toFixed(2)}`, 'Custo']} />
                          <Bar dataKey="total_custo" name="Custo Total" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                      {/* Team summary below chart */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3">
                        {consumoEquipes.por_equipe.map(eq => (
                          <div key={eq.equipe_id} className="text-center p-2 bg-muted/30 rounded-lg">
                            <p className="text-sm font-medium truncate">{eq.equipe_nome}</p>
                            <p className="text-xs text-muted-foreground">{eq.total_pacientes} pac · {eq.total_itens} itens</p>
                            <p className="text-sm font-bold">{formatCurrency(eq.total_custo)}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Patient consumption table */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Consumo por Paciente Internado
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-0 pt-0">
                  {(consumoPacientes.consumo_por_paciente || []).length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Paciente</TableHead>
                          <TableHead className="text-right w-28">Itens</TableHead>
                          <TableHead className="text-right w-24">Movim.</TableHead>
                          {consumoPacientes.total_geral_custo != null && (
                            <TableHead className="text-right w-36">Custo Total</TableHead>
                          )}
                          <TableHead className="w-36">Último Consumo</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {consumoPacientes.consumo_por_paciente.map((r) => (
                          <TableRow key={r.internacao_id}>
                            <TableCell className="font-medium">{r.paciente_nome || 'N/A'}</TableCell>
                            <TableCell className="text-right">{r.total_itens}</TableCell>
                            <TableCell className="text-right">{r.movimentacoes}</TableCell>
                            {consumoPacientes.total_geral_custo != null && (
                              <TableCell className="text-right font-mono">{formatCurrency(r.total_custo)}</TableCell>
                            )}
                            <TableCell className="text-xs text-muted-foreground">
                              {r.ultima_movimentacao ? new Date(r.ultima_movimentacao).toLocaleDateString('pt-BR') : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="py-8 text-center text-muted-foreground">
                      <Users className="h-10 w-10 mx-auto mb-2 opacity-30" />
                      <p>Nenhum consumo registrado por paciente</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          ) : (
            <Card><CardContent className="py-12 text-center text-muted-foreground">
              <Users className="h-10 w-10 mx-auto mb-2 opacity-30" />
              <p>Carregando dados de consumo...</p>
            </CardContent></Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Item Dialog */}
      <Dialog open={showItemDialog} onOpenChange={setShowItemDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Editar Item' : 'Novo Item'}</DialogTitle>
            <DialogDescription>
              {editingItem ? 'Edite as informações do item' : 'Cadastre um novo item no estoque'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo *</Label>
                <Select value={itemForm.tipo || 'material'} onValueChange={(v) => setItemForm(prev => ({ ...prev, tipo: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder={itemForm.tipo || 'Selecione'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="material">Material</SelectItem>
                    <SelectItem value="medicamento">Medicamento</SelectItem>
                    {itemForm.tipo && !['material', 'medicamento'].includes(itemForm.tipo) && (
                      <SelectItem value={itemForm.tipo}>{itemForm.tipo}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Unidade *</Label>
                <Select value={itemForm.unidade} onValueChange={(v) => setItemForm(prev => ({ ...prev, unidade: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder={itemForm.unidade || 'Selecione'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="un">Unidade (un)</SelectItem>
                    <SelectItem value="cx">Caixa (cx)</SelectItem>
                    <SelectItem value="ml">Mililitro (ml)</SelectItem>
                    <SelectItem value="mg">Miligrama (mg)</SelectItem>
                    <SelectItem value="g">Grama (g)</SelectItem>
                    <SelectItem value="lt">Litro (lt)</SelectItem>
                    <SelectItem value="ampola">Ampola</SelectItem>
                    <SelectItem value="frasco">Frasco</SelectItem>
                    <SelectItem value="comprimido">Comprimido</SelectItem>
                    <SelectItem value="pct">Pacote (pct)</SelectItem>
                    <SelectItem value="par">Par</SelectItem>
                    <SelectItem value="kit">Kit</SelectItem>
                    <SelectItem value="rolo">Rolo</SelectItem>
                    <SelectItem value="tubo">Tubo</SelectItem>
                    <SelectItem value="capsula">Cápsula</SelectItem>
                    <SelectItem value="bisnaga">Bisnaga</SelectItem>
                    <SelectItem value="sache">Sachê</SelectItem>
                    {itemForm.unidade && ![
                      'un','cx','ml','mg','g','lt','ampola','frasco','comprimido',
                      'pct','par','kit','rolo','tubo','capsula','bisnaga','sache'
                    ].includes(itemForm.unidade) && (
                      <SelectItem value={itemForm.unidade}>{itemForm.unidade}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Nome do item *</Label>
              <Input
                value={itemForm.nome}
                onChange={(e) => setItemForm(prev => ({ ...prev, nome: e.target.value }))}
                placeholder="Ex: Dipirona 500mg"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Categoria</Label>
                <Input
                  value={itemForm.categoria}
                  onChange={(e) => setItemForm(prev => ({ ...prev, categoria: e.target.value }))}
                  placeholder="Ex: Analgésicos"
                />
              </div>
              <div className="space-y-2">
                <Label>Fabricante</Label>
                <Input
                  value={itemForm.fabricante}
                  onChange={(e) => setItemForm(prev => ({ ...prev, fabricante: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Código interno</Label>
                <Input
                  value={itemForm.codigo_interno}
                  onChange={(e) => setItemForm(prev => ({ ...prev, codigo_interno: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Código de barras</Label>
                <Input
                  value={itemForm.codigo_barras}
                  onChange={(e) => setItemForm(prev => ({ ...prev, codigo_barras: e.target.value }))}
                />
              </div>
            </div>
            {itemForm.tipo === 'medicamento' && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Concentração</Label>
                  <Input
                    value={itemForm.concentracao}
                    onChange={(e) => setItemForm(prev => ({ ...prev, concentracao: e.target.value }))}
                    placeholder="Ex: 500mg"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Forma</Label>
                  <Input
                    value={itemForm.forma}
                    onChange={(e) => setItemForm(prev => ({ ...prev, forma: e.target.value }))}
                    placeholder="Ex: Comprimido"
                  />
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label>Estoque mínimo</Label>
              <Input
                type="number"
                value={itemForm.estoque_minimo}
                onChange={(e) => setItemForm(prev => ({ ...prev, estoque_minimo: e.target.value }))}
                placeholder="0"
              />
            </div>
            <div className="space-y-2">
              <Label>Custo médio (R$)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={itemForm.custo_medio}
                onChange={(e) => setItemForm(prev => ({ ...prev, custo_medio: e.target.value }))}
                placeholder="0,00"
                data-testid="input-custo-medio"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Lote</Label>
                <Input
                  value={itemForm.lote || ''}
                  onChange={(e) => setItemForm(prev => ({ ...prev, lote: e.target.value }))}
                  placeholder="Nº do lote"
                  data-testid="input-item-lote"
                />
              </div>
              <div className="space-y-2">
                <Label>Validade</Label>
                <Input
                  type="date"
                  value={itemForm.validade || ''}
                  onChange={(e) => setItemForm(prev => ({ ...prev, validade: e.target.value }))}
                  data-testid="input-item-validade"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowItemDialog(false)}>Cancelar</Button>
            <Button onClick={handleItemSubmit} disabled={!itemForm.nome}>
              {editingItem ? 'Salvar' : 'Criar Item'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Entrada Dialog */}
      <Dialog open={showEntradaDialog} onOpenChange={setShowEntradaDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nova Entrada (Manual)</DialogTitle>
            <DialogDescription>Registre uma entrada de estoque manualmente</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Fornecedor</Label>
                <Input
                  value={entradaForm.fornecedor}
                  onChange={(e) => setEntradaForm(prev => ({ ...prev, fornecedor: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Documento/NF</Label>
                <Input
                  value={entradaForm.documento_ref}
                  onChange={(e) => setEntradaForm(prev => ({ ...prev, documento_ref: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Data</Label>
                <Input
                  type="date"
                  value={entradaForm.data_entrada}
                  onChange={(e) => setEntradaForm(prev => ({ ...prev, data_entrada: e.target.value }))}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label>Itens</Label>
                <Button variant="outline" size="sm" onClick={addEntradaLine}>
                  <Plus className="h-4 w-4 mr-1" /> Adicionar linha
                </Button>
              </div>
              <div className="border rounded-md p-4 space-y-3">
                {entradaForm.itens.map((linha, index) => (
                  <div key={index} className="grid grid-cols-5 gap-2 items-end">
                    <div className="col-span-2">
                      <Label className="text-xs">Item</Label>
                      <Select
                        value={linha.item_id}
                        onValueChange={(v) => updateEntradaLine(index, 'item_id', v)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione..." />
                        </SelectTrigger>
                        <SelectContent>
                          {itens.map(item => (
                            <SelectItem key={item.id} value={item.id}>
                              {item.nome}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Qtd</Label>
                      <Input
                        type="number"
                        value={linha.quantidade}
                        onChange={(e) => updateEntradaLine(index, 'quantidade', e.target.value)}
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Valor Unit.</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={linha.valor_unitario}
                        onChange={(e) => updateEntradaLine(index, 'valor_unitario', e.target.value)}
                        placeholder="0,00"
                      />
                    </div>
                    <div className="flex gap-1">
                      <div className="flex-1">
                        <Label className="text-xs">Total</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={linha.valor_total}
                          onChange={(e) => updateEntradaLine(index, 'valor_total', e.target.value)}
                          placeholder="0,00"
                        />
                      </div>
                      {entradaForm.itens.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="mt-5"
                          onClick={() => removeEntradaLine(index)}
                        >
                          <X className="h-4 w-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                    {/* Lote e Validade */}
                    <div>
                      <Label className="text-xs">Lote</Label>
                      <Input
                        value={linha.lote || ''}
                        onChange={(e) => updateEntradaLine(index, 'lote', e.target.value)}
                        placeholder="Nº do lote"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Validade</Label>
                      <Input
                        type="date"
                        value={linha.validade || ''}
                        onChange={(e) => updateEntradaLine(index, 'validade', e.target.value)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Observações</Label>
              <Textarea
                value={entradaForm.observacoes}
                onChange={(e) => setEntradaForm(prev => ({ ...prev, observacoes: e.target.value }))}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEntradaDialog(false)}>Cancelar</Button>
            <Button onClick={handleEntradaSubmit}>
              <Check className="h-4 w-4 mr-2" />
              Confirmar Entrada
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Saída Dialog */}
      <Dialog open={showSaidaDialog} onOpenChange={setShowSaidaDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nova Saída</DialogTitle>
            <DialogDescription>Registre uma saída manual de estoque</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Item *</Label>
              <Select
                value={saidaForm.item_id}
                onValueChange={(v) => setSaidaForm(prev => ({ ...prev, item_id: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o item..." />
                </SelectTrigger>
                <SelectContent>
                  {itens.map(item => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.nome} ({item.estoque_atual} {item.unidade})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Quantidade *</Label>
              <Input
                type="number"
                value={saidaForm.quantidade}
                onChange={(e) => setSaidaForm(prev => ({ ...prev, quantidade: e.target.value }))}
                placeholder="0"
              />
            </div>
            <div className="space-y-2">
              <Label>Motivo</Label>
              <Input
                value={saidaForm.motivo}
                onChange={(e) => setSaidaForm(prev => ({ ...prev, motivo: e.target.value }))}
                placeholder="Ex: Uso interno, descarte, etc."
              />
            </div>
            <div className="space-y-2">
              <Label>Referência</Label>
              <Input
                value={saidaForm.referencia}
                onChange={(e) => setSaidaForm(prev => ({ ...prev, referencia: e.target.value }))}
                placeholder="Ex: OS 12345"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaidaDialog(false)}>Cancelar</Button>
            <Button onClick={handleSaidaSubmit} disabled={!saidaForm.item_id || !saidaForm.quantidade}>
              Registrar Saída
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Importar Documento (IA)
            </DialogTitle>
            <DialogDescription>
              Importe documentos para extração automática de dados
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Arquivo</Label>
              <Input
                type="file"
                accept=".xml,.pdf,.jpg,.jpeg,.png,.webp,.csv,.xlsx,.xls"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                data-testid="input-import-file"
              />
              <p className="text-xs text-muted-foreground">
                Formatos aceitos: XML (NF-e), CSV, Excel, PDF, Imagens
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              <div className="p-3 rounded-lg bg-muted/50 text-center">
                <FileText className="h-8 w-8 mx-auto mb-1 text-blue-500" />
                <p className="text-xs">XML/PDF</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/50 text-center">
                <FileSpreadsheet className="h-8 w-8 mx-auto mb-1 text-green-500" />
                <p className="text-xs">CSV/Excel</p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportDialog(false)}>Cancelar</Button>
            <Button 
              onClick={() => handleImportFile(importFile)} 
              disabled={!importFile || importLoading}
              data-testid="btn-processar-import"
            >
              {importLoading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Processando...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Processar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Column Mapping Dialog (for CSV/Excel) */}
      <Dialog open={showColumnMappingDialog} onOpenChange={setShowColumnMappingDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5" />
              Mapear Colunas
            </DialogTitle>
            <DialogDescription>
              Identifique as colunas do seu arquivo
            </DialogDescription>
          </DialogHeader>
          
          {columnData && (
            <div className="space-y-4 py-4">
              <p className="text-sm text-muted-foreground">
                Colunas detectadas: {columnData.colunas?.join(', ')}
              </p>
              
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Nome do Produto *</Label>
                  <Select 
                    value={columnMapping.nome} 
                    onValueChange={(v) => setColumnMapping(prev => ({ ...prev, nome: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione..." />
                    </SelectTrigger>
                    <SelectContent>
                      {columnData.colunas?.map(col => (
                        <SelectItem key={col} value={col}>{col}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Quantidade *</Label>
                  <Select 
                    value={columnMapping.quantidade} 
                    onValueChange={(v) => setColumnMapping(prev => ({ ...prev, quantidade: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione..." />
                    </SelectTrigger>
                    <SelectContent>
                      {columnData.colunas?.map(col => (
                        <SelectItem key={col} value={col}>{col}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Valor Unitário</Label>
                  <Select 
                    value={columnMapping.valor_unitario} 
                    onValueChange={(v) => setColumnMapping(prev => ({ ...prev, valor_unitario: v === '_none_' ? '' : v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="(Opcional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none_">Nenhum</SelectItem>
                      {columnData.colunas?.map(col => (
                        <SelectItem key={col} value={col}>{col}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Lote</Label>
                  <Select 
                    value={columnMapping.lote} 
                    onValueChange={(v) => setColumnMapping(prev => ({ ...prev, lote: v === '_none_' ? '' : v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="(Opcional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none_">Nenhum</SelectItem>
                      {columnData.colunas?.map(col => (
                        <SelectItem key={col} value={col}>{col}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Validade</Label>
                  <Select 
                    value={columnMapping.validade} 
                    onValueChange={(v) => setColumnMapping(prev => ({ ...prev, validade: v === '_none_' ? '' : v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="(Opcional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none_">Nenhum</SelectItem>
                      {columnData.colunas?.map(col => (
                        <SelectItem key={col} value={col}>{col}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Sample data preview */}
              {columnData.amostra?.length > 0 && (
                <div className="mt-4">
                  <Label className="text-xs text-muted-foreground">Amostra dos dados:</Label>
                  <div className="mt-1 overflow-x-auto">
                    <Table className="text-xs">
                      <TableHeader>
                        <TableRow>
                          {columnData.colunas?.map(col => (
                            <TableHead key={col} className="px-2 py-1">{col}</TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {columnData.amostra.slice(0, 2).map((row, idx) => (
                          <TableRow key={idx}>
                            {columnData.colunas?.map(col => (
                              <TableCell key={col} className="px-2 py-1">{row[col]}</TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowColumnMappingDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={() => processImportFile(importFile, columnMapping)}
              disabled={!columnMapping.nome || !columnMapping.quantidade || importLoading}
            >
              {importLoading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Processando...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Processar com Mapeamento
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review Import Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Revisar Entrada
            </DialogTitle>
            <DialogDescription>
              Revise e complete os dados antes de confirmar. Campos em vermelho são obrigatórios.
            </DialogDescription>
          </DialogHeader>
          {importedData && (
            <div className="space-y-4 py-4">
              {/* Alerts */}
              {importedData.alertas?.length > 0 && (
                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-amber-700 dark:text-amber-400">Atenção</p>
                      <ul className="text-sm text-amber-600 dark:text-amber-400 list-disc list-inside">
                        {importedData.alertas.map((alerta, idx) => (
                          <li key={idx}>{alerta}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Header info */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Fornecedor</Label>
                  <Input 
                    value={importedData.fornecedor || ''} 
                    onChange={(e) => setImportedData(prev => ({ ...prev, fornecedor: e.target.value }))}
                    placeholder="Nome do fornecedor"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Documento</Label>
                  <Input 
                    value={importedData.documento_ref || ''} 
                    onChange={(e) => setImportedData(prev => ({ ...prev, documento_ref: e.target.value }))}
                    placeholder="Nº da NF"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Data</Label>
                  <Input 
                    type="date"
                    value={importedData.data_documento || ''} 
                    onChange={(e) => setImportedData(prev => ({ ...prev, data_documento: e.target.value }))}
                  />
                </div>
              </div>
              
              {/* Items table */}
              <ScrollArea className="h-[400px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[200px]">Item</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead className="text-right">Qtd</TableHead>
                      <TableHead className="text-right">Valor Unit.</TableHead>
                      <TableHead>Lote</TableHead>
                      <TableHead>Validade</TableHead>
                      <TableHead>Vínculo</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {importedData.itens?.map((item, index) => (
                      <TableRow key={index} className={item.tipo === 'medicamento' && !item.validade ? 'bg-red-50 dark:bg-red-900/10' : ''}>
                        <TableCell>
                          <Input 
                            value={item.item_nome}
                            onChange={(e) => updateImportedItem(index, 'item_nome', e.target.value)}
                            className="h-8 text-sm"
                          />
                        </TableCell>
                        <TableCell>
                          <Select 
                            value={item.tipo} 
                            onValueChange={(v) => updateImportedItem(index, 'tipo', v)}
                          >
                            <SelectTrigger className="h-8 w-[110px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="material">Material</SelectItem>
                              <SelectItem value="medicamento">Medicamento</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Input 
                            type="number"
                            value={item.quantidade}
                            onChange={(e) => updateImportedItem(index, 'quantidade', parseFloat(e.target.value) || 0)}
                            className="h-8 text-sm text-right w-20"
                          />
                        </TableCell>
                        <TableCell>
                          <Input 
                            type="number"
                            step="0.01"
                            value={item.valor_unitario || ''}
                            onChange={(e) => updateImportedItem(index, 'valor_unitario', parseFloat(e.target.value) || null)}
                            className="h-8 text-sm text-right w-24"
                            placeholder="0.00"
                          />
                        </TableCell>
                        <TableCell>
                          <Input 
                            value={item.lote || ''}
                            onChange={(e) => updateImportedItem(index, 'lote', e.target.value)}
                            className="h-8 text-sm w-28"
                            placeholder="Nº lote"
                          />
                        </TableCell>
                        <TableCell>
                          <Input 
                            type="date"
                            value={item.validade || ''}
                            onChange={(e) => updateImportedItem(index, 'validade', e.target.value)}
                            className={`h-8 text-sm w-36 ${item.tipo === 'medicamento' && !item.validade ? 'border-red-500' : ''}`}
                          />
                        </TableCell>
                        <TableCell>
                          {item.item_id ? (
                            <Badge variant="default" className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                              <Check className="h-3 w-3 mr-1" />
                              Existe
                            </Badge>
                          ) : (
                            <Badge variant="secondary">
                              <Plus className="h-3 w-3 mr-1" />
                              Novo
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
              
              {/* Confidence indicator */}
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>Formato detectado: {importedData.formato_detectado?.toUpperCase()}</span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowReviewDialog(false); setImportedData(null); }}>
              Cancelar
            </Button>
            <Button onClick={handleConfirmImport} disabled={importLoading} data-testid="btn-confirmar-import">
              {importLoading ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              Confirmar e Lançar Entrada
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lotes Dialog */}
      <Dialog open={showLotesDialog} onOpenChange={setShowLotesDialog}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="h-5 w-5 text-orange-500" />
              Lotes e Validade - {selectedItemLotes?.item?.nome}
            </DialogTitle>
            <DialogDescription>
              Controle de lotes e validade para este item
            </DialogDescription>
          </DialogHeader>

          {selectedItemLotes && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-4 gap-4">
                <div className="p-3 bg-muted rounded-lg text-center">
                  <p className="text-2xl font-bold">{selectedItemLotes.lotes?.length || 0}</p>
                  <p className="text-xs text-muted-foreground">Total de Lotes</p>
                </div>
                <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-600">{selectedItemLotes.resumo?.ok || 0}</p>
                  <p className="text-xs text-muted-foreground">OK</p>
                </div>
                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg text-center">
                  <p className="text-2xl font-bold text-amber-600">{selectedItemLotes.resumo?.vence_em_breve || 0}</p>
                  <p className="text-xs text-muted-foreground">Vence em breve</p>
                </div>
                <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-center">
                  <p className="text-2xl font-bold text-red-600">{selectedItemLotes.resumo?.vencido || 0}</p>
                  <p className="text-xs text-muted-foreground">Vencidos</p>
                </div>
              </div>

              {/* Lotes Table */}
              <ScrollArea className="h-[300px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Lote</TableHead>
                      <TableHead>Validade</TableHead>
                      <TableHead>Dias p/ Vencer</TableHead>
                      <TableHead className="text-right">Quantidade</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedItemLotes.lotes?.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                          Nenhum lote cadastrado para este item
                        </TableCell>
                      </TableRow>
                    ) : (
                      selectedItemLotes.lotes?.map((lote) => (
                        <TableRow key={lote.id}>
                          <TableCell className="font-medium">{lote.lote}</TableCell>
                          <TableCell>
                            {lote.validade ? (
                              <div className="flex items-center gap-1">
                                <Calendar className="h-3 w-3 text-muted-foreground" />
                                {/^\d{4}-\d{2}-\d{2}$/.test(lote.validade) ? new Date(lote.validade + 'T12:00:00').toLocaleDateString('pt-BR') : new Date(lote.validade).toLocaleDateString('pt-BR')}
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {lote.dias_para_vencer !== null ? (
                              <span className={lote.dias_para_vencer < 0 ? 'text-red-600 font-medium' : 
                                             lote.dias_para_vencer <= 30 ? 'text-amber-600 font-medium' : ''}>
                                {lote.dias_para_vencer < 0 ? `Vencido há ${Math.abs(lote.dias_para_vencer)} dias` : 
                                 `${lote.dias_para_vencer} dias`}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {lote.quantidade_atual} {selectedItemLotes.item?.unidade || 'un'}
                          </TableCell>
                          <TableCell>
                            <Badge className={VALIDADE_STATUS_COLORS[lote.status]}>
                              {VALIDADE_STATUS_LABELS[lote.status]}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>

              {/* Total */}
              <div className="flex justify-between items-center p-3 bg-muted/50 rounded-lg">
                <span className="font-medium">Total em lotes:</span>
                <span className="text-lg font-bold">
                  {selectedItemLotes.total_em_lotes || 0} {selectedItemLotes.item?.unidade || 'un'}
                </span>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLotesDialog(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Entrada Dialog */}
      <Dialog open={showEditEntradaDialog} onOpenChange={setShowEditEntradaDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Entrada</DialogTitle>
            <DialogDescription>
              Edite as informações da entrada de estoque
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Fornecedor</Label>
              <Input
                value={editEntradaForm.fornecedor}
                onChange={(e) => setEditEntradaForm({ ...editEntradaForm, fornecedor: e.target.value })}
                placeholder="Nome do fornecedor"
                data-testid="edit-entrada-fornecedor"
              />
            </div>
            <div>
              <Label>Documento de Referência</Label>
              <Input
                value={editEntradaForm.documento_ref}
                onChange={(e) => setEditEntradaForm({ ...editEntradaForm, documento_ref: e.target.value })}
                placeholder="Número da NF, pedido, etc."
                data-testid="edit-entrada-documento"
              />
            </div>
            <div>
              <Label>Data da Entrada</Label>
              <Input
                type="date"
                value={editEntradaForm.data_entrada}
                onChange={(e) => setEditEntradaForm({ ...editEntradaForm, data_entrada: e.target.value })}
                data-testid="edit-entrada-data"
              />
            </div>
            <div>
              <Label>Observações</Label>
              <Textarea
                value={editEntradaForm.observacoes}
                onChange={(e) => setEditEntradaForm({ ...editEntradaForm, observacoes: e.target.value })}
                placeholder="Observações sobre a entrada"
                rows={3}
                data-testid="edit-entrada-observacoes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditEntradaDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveEntrada} data-testid="save-entrada-btn">
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Scanner Entrada Modal */}
      <ScannerEntradaModal 
        open={showScannerModal}
        onClose={() => setShowScannerModal(false)}
        onEntryCreated={() => {
          fetchData();
          fetchItens();
        }}
      />

      {/* Zerar Estoque Modal */}
      <Dialog open={showZerarDialog} onOpenChange={setShowZerarDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-700">
              <AlertTriangle className="h-5 w-5" />
              Zerar estoque
            </DialogTitle>
            <DialogDescription>
              Esta ação ajustará o estoque atual para 0. Confirma?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="font-semibold">Motivo do ajuste *</Label>
              <Textarea
                value={zerarForm.motivo}
                onChange={(e) => setZerarForm({ ...zerarForm, motivo: e.target.value })}
                placeholder="Descreva o motivo do ajuste..."
                rows={3}
                className="resize-none"
                data-testid="textarea-zerar-motivo"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-semibold text-red-700">
                Digite "ZERAR" para confirmar *
              </Label>
              <Input
                value={zerarForm.confirmacao}
                onChange={(e) => setZerarForm({ ...zerarForm, confirmacao: e.target.value })}
                placeholder="ZERAR"
                data-testid="input-zerar-confirmacao"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowZerarDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleZerarEstoque}
              disabled={savingZerar || zerarForm.confirmacao !== 'ZERAR' || zerarForm.motivo.trim().length < 5}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="btn-confirmar-zerar"
            >
              {savingZerar ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reverter Baixas Automáticas Modal */}
      <Dialog open={showReverterDialog} onOpenChange={setShowReverterDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <RotateCcw className="h-5 w-5" />
              Reverter baixas automáticas
            </DialogTitle>
            <DialogDescription>
              Identifica e reverte movimentações de estoque geradas automaticamente pelo antigo sistema de consumo diário.
            </DialogDescription>
          </DialogHeader>
          {reverterInfo && (
            <div className="space-y-3 py-2">
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold">{reverterInfo.total}</p>
                  <p className="text-xs text-muted-foreground">Total</p>
                </div>
                <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-amber-600">{reverterInfo.pendentes_reversao}</p>
                  <p className="text-xs text-muted-foreground">Pendentes</p>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-600">{reverterInfo.ja_revertidos}</p>
                  <p className="text-xs text-muted-foreground">Já revertidos</p>
                </div>
              </div>
              {reverterInfo.pendentes_reversao === 0 && (
                <p className="text-sm text-green-600 text-center">Nenhuma movimentação pendente de reversão.</p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReverterDialog(false)}>
              Fechar
            </Button>
            {reverterInfo && reverterInfo.pendentes_reversao > 0 && (
              <Button
                onClick={handleReverterBaixas}
                disabled={loadingReverter}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="btn-confirmar-reverter"
              >
                {loadingReverter ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Reverter {reverterInfo.pendentes_reversao} baixa(s)
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Custos CSV Dialog */}
      <Dialog open={showImportCustosDialog} onOpenChange={setShowImportCustosDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Importar Custos via CSV</DialogTitle>
            <DialogDescription>
              Faça upload de um CSV com colunas: <strong>nome</strong> (ou codigo_interno) e <strong>custo_medio</strong>.
              Os itens serão localizados pelo nome/código e o custo médio será atualizado.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <input
                type="file"
                accept=".csv"
                data-testid="input-csv-custos"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = (ev) => {
                    const text = ev.target.result;
                    const lines = text.split('\n').filter(l => l.trim());
                    if (lines.length < 2) { toast.error('CSV vazio ou sem dados'); return; }
                    const header = lines[0].split(/[;,]/).map(h => h.trim().toLowerCase().replace(/['"]/g, ''));
                    const nomeIdx = header.findIndex(h => h === 'nome' || h === 'item' || h === 'produto');
                    const codIdx = header.findIndex(h => h === 'codigo_interno' || h === 'codigo' || h === 'cod');
                    const custoIdx = header.findIndex(h => h === 'custo_medio' || h === 'custo' || h === 'valor' || h === 'preco');
                    if (custoIdx === -1) { toast.error('Coluna de custo não encontrada. Use "custo_medio", "custo" ou "valor"'); return; }
                    if (nomeIdx === -1 && codIdx === -1) { toast.error('Coluna de identificação não encontrada. Use "nome" ou "codigo_interno"'); return; }
                    const parsed = [];
                    for (let i = 1; i < lines.length; i++) {
                      const cols = lines[i].split(/[;,]/).map(c => c.trim().replace(/['"]/g, ''));
                      const custo = parseFloat(cols[custoIdx]?.replace(',', '.'));
                      if (isNaN(custo) || custo < 0) continue;
                      parsed.push({
                        nome: nomeIdx >= 0 ? cols[nomeIdx] : null,
                        codigo_interno: codIdx >= 0 ? cols[codIdx] : null,
                        custo_medio: custo
                      });
                    }
                    setCustosCSVData(parsed);
                    toast.success(`${parsed.length} itens encontrados no CSV`);
                  };
                  reader.readAsText(file);
                }}
                className="block w-full text-sm border rounded-md p-2 file:mr-4 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-sm file:bg-primary file:text-primary-foreground"
              />
            </div>
            {custosCSVData.length > 0 && (
              <div className="max-h-[200px] overflow-y-auto border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome / Código</TableHead>
                      <TableHead className="text-right">Custo Médio</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {custosCSVData.slice(0, 20).map((r, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="text-sm">{r.nome || r.codigo_interno || '-'}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{formatCurrency(r.custo_medio)}</TableCell>
                      </TableRow>
                    ))}
                    {custosCSVData.length > 20 && (
                      <TableRow><TableCell colSpan={2} className="text-center text-xs text-muted-foreground">... e mais {custosCSVData.length - 20} itens</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowImportCustosDialog(false); setCustosCSVData([]); }}>Cancelar</Button>
            <Button
              disabled={custosCSVData.length === 0 || importandoCustos}
              data-testid="btn-processar-custos"
              onClick={async () => {
                setImportandoCustos(true);
                try {
                  const res = await api.post('/estoque/importar-custos/processar', { itens: custosCSVData });
                  const d = res.data;
                  toast.success(`${d.atualizados} itens atualizados`);
                  if (d.nao_encontrados?.length > 0) toast.warning(`Não encontrados: ${d.nao_encontrados.join(', ')}`);
                  if (d.erros?.length > 0) toast.error(`Erros: ${d.erros.join(', ')}`);
                  setShowImportCustosDialog(false);
                  setCustosCSVData([]);
                  fetchData();
                } catch (err) { toast.error('Erro ao processar importação'); }
                finally { setImportandoCustos(false); }
              }}
            >
              {importandoCustos ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Processar {custosCSVData.length} itens
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </Layout>
  );
};

export default Estoque;
